import { Tooltip } from '@design/components/tooltip/Tooltip';
import { MouseEvent, ReactNode, RefObject, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './MentionComposerInput.module.scss';
import { ActiveTrigger } from './trigger-detection';
import {
  findUnresolvedMentionFromTarget,
  readUnresolvedMentionText,
  unresolvedElementToTrigger,
} from './unresolved-mention';

export interface UseUnresolvedMentionsArgs {
  editorRef: RefObject<HTMLDivElement | null>;
  applyTrigger: (trigger: ActiveTrigger) => void;
  /** Hint shown while hovering an unresolved (multi-match) mention token. */
  unresolvedHintText?: (mentionText: string) => ReactNode;
}

export interface UseUnresolvedMentionsResult {
  openUnresolvedFromEvent: (e: MouseEvent<HTMLDivElement>) => boolean;
  onUnresolvedMouseOver: (e: MouseEvent<HTMLDivElement>) => void;
  onUnresolvedMouseOut: (e: MouseEvent<HTMLDivElement>) => void;
  clearHoveredUnresolved: () => void;
  unresolvedTooltip: ReactNode;
}

const defaultUnresolvedHintText = (mentionText: string): string =>
  `Several matches for “${mentionText}”`;

export const useUnresolvedMentions = ({
  editorRef,
  applyTrigger,
  unresolvedHintText = defaultUnresolvedHintText,
}: UseUnresolvedMentionsArgs): UseUnresolvedMentionsResult => {
  const [hoveredUnresolved, setHoveredUnresolved] =
    useState<HTMLElement | null>(null);
  const [unresolvedTooltipRect, setUnresolvedTooltipRect] =
    useState<DOMRect | null>(null);

  const openUnresolvedFromEvent = (e: MouseEvent<HTMLDivElement>) => {
    const root = editorRef.current;
    if (!root) {
      return false;
    }
    const el = findUnresolvedMentionFromTarget(root, e.target);
    if (!el) {
      return false;
    }
    const trigger = unresolvedElementToTrigger(el);
    if (!trigger) {
      return false;
    }
    applyTrigger(trigger);
    return true;
  };

  const onUnresolvedMouseOver = (e: MouseEvent<HTMLDivElement>) => {
    const root = editorRef.current;
    if (!root) {
      return;
    }
    const el = findUnresolvedMentionFromTarget(root, e.target);
    if (el && el !== hoveredUnresolved) {
      setHoveredUnresolved(el);
      setUnresolvedTooltipRect(el.getBoundingClientRect());
    }
  };

  const onUnresolvedMouseOut = (e: MouseEvent<HTMLDivElement>) => {
    const root = editorRef.current;
    if (!root || !hoveredUnresolved) {
      return;
    }
    const leaving = findUnresolvedMentionFromTarget(root, e.target);
    if (!leaving || leaving !== hoveredUnresolved) {
      return;
    }
    const related = e.relatedTarget;
    if (related instanceof Node && hoveredUnresolved.contains(related)) {
      return;
    }
    setHoveredUnresolved(null);
    setUnresolvedTooltipRect(null);
  };

  const clearHoveredUnresolved = () => {
    setHoveredUnresolved(null);
    setUnresolvedTooltipRect(null);
  };

  // See the matching note in useResolvedMentionPreview: `inPortal` only
  // relocates the tooltip bubble, not this anchor span, so `createPortal`
  // is needed to keep the anchor's `position: fixed` viewport-relative
  // under a transformed ancestor (e.g. a Drawer's slide-in animation).
  const unresolvedTooltip =
    hoveredUnresolved?.isConnected && unresolvedTooltipRect
      ? createPortal(
          <Tooltip
            label={unresolvedHintText(
              readUnresolvedMentionText(hoveredUnresolved)
            )}
            isOpen
            inPortal
            strategy="fixed"
            placement="top"
            hasDelay={false}
            dismissible={false}
            trigger={[]}
          >
            <span
              aria-hidden
              className={styles.unresolvedTooltipAnchor}
              style={{
                top: unresolvedTooltipRect.top,
                left: unresolvedTooltipRect.left,
                width: unresolvedTooltipRect.width,
                height: unresolvedTooltipRect.height,
              }}
            />
          </Tooltip>,
          document.body
        )
      : null;

  return {
    openUnresolvedFromEvent,
    onUnresolvedMouseOver,
    onUnresolvedMouseOut,
    clearHoveredUnresolved,
    unresolvedTooltip,
  };
};
